import { Link, useParams } from 'react-router';
import { formatEuro, type PublicEvent } from '@avh/shared';
import { Container } from '@/components/layout/PublicLayout';
import { ApiStateView } from '@/components/ui/ApiStateView';
import { Card } from '@/components/ui/Feedback';
import { useI18n } from '@/contexts/I18n';
import { formatDateTime, formatTime, localized } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { AvailabilityNote } from '@/pages/public/AvailabilityNote';
import { TicketForm } from '@/pages/public/TicketForm';

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-neutral-200 py-3 last:border-0 sm:flex-row sm:gap-6">
      <dt className="w-28 shrink-0 text-sm font-semibold text-muted">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function EventDetails({ event }: { event: PublicEvent }) {
  const { t, locale } = useI18n();
  const description = localized(locale, event.description, event.descriptionEn);
  const isFree = event.priceCents === 0;
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_26rem]">
      <div>
        <Link to="/tickets" className="text-sm font-semibold text-brand-600 hover:underline">
          ← {t.event.back}
        </Link>
        <h1 className="mt-3 text-3xl font-bold text-brand-900 sm:text-4xl">{localized(locale, event.title, event.titleEn)}</h1>
        {event.subtitle ? (
          <p className="mt-2 text-lg text-brand-700">{localized(locale, event.subtitle, event.subtitleEn)}</p>
        ) : null}
        {event.imageUrl ? <img src={event.imageUrl} alt="" className="mt-6 w-full rounded-lg object-cover" /> : null}
        <dl className="mt-6 rounded-lg bg-white px-5 shadow-sm ring-1 ring-black/5">
          <Fact label={t.event.when}>{formatDateTime(event.startsAt, locale)}</Fact>
          {event.doorsAt ? <Fact label={t.event.doors}>{formatTime(event.doorsAt, locale)}</Fact> : null}
          <Fact label={t.event.where}>
            {event.venueName}
            {event.venueAddress ? <span className="block text-sm text-muted">{event.venueAddress}</span> : null}
          </Fact>
          <Fact label={t.event.price}>
            {isFree ? t.event.free : `${formatEuro(event.priceCents, locale)} ${t.event.perTicket}`}
            <span className="block text-sm text-muted">{t.event.freeSeating}</span>
          </Fact>
        </dl>
        {description ? (
          <div className="mt-6 space-y-4 leading-relaxed">
            {description.split(/\n{2,}/).map((paragraph) => (
              <p key={paragraph} className="whitespace-pre-line">
                {paragraph}
              </p>
            ))}
          </div>
        ) : null}
      </div>
      <div className="lg:sticky lg:top-6 lg:self-start">
        <Card>
          <h2 className="mb-1 text-xl font-bold text-brand-900">{isFree ? t.event.registerTitle : t.event.buyTitle}</h2>
          <div className="mb-4">
            <AvailabilityNote event={event} />
          </div>
          {event.salesState === 'open' ? <TicketForm event={event} /> : null}
        </Card>
      </div>
    </div>
  );
}

export function EventPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const [state, reload] = useApi<PublicEvent>(`/public/events/${encodeURIComponent(id)}`);
  return (
    <Container>
      <ApiStateView state={state} onRetry={reload} loadingLabel={t.common.loading} retryLabel={t.common.retry}>
        {(event) => <EventDetails event={event} />}
      </ApiStateView>
    </Container>
  );
}
